import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button, Confirm, Modal, NotFound, Typography, Wrapper } from '@/components';
import { useAuthUser, useTeamGet, useTeamMemberRemove, useUserGet } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { fixUrlForCloudFront } from '@/utils/cloudfront';

import styles from './Team.module.css';
import { TeamBrandSettingsSection } from './TeamBrandSettingsSection';
import { TeamBrands } from './TeamBrands';
import { TeamHeader } from './TeamHeader';
import { TeamInvite } from './TeamInvite';
import { TeamMember } from './TeamMember';

export function Team() {
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [members, setMembers] = useState<Api.Member[]>([]);
  const [leaveConfirm, setLeaveConfirm] = useState(false);
  const [brands, setBrands] = useState<Api.Brand[]>([]);
  const [teams, setTeams] = useState<Api.Team[]>([]);
  const router = useRouter();
  const user = useAuthUser();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading: isLoadingTeams } = useTeamGet(user?.team?.team_id);

  const { fetchUser } = useUserGet();

  const { mutation: teamMemberRemove, isLoading: isRemovingMember } = useTeamMemberRemove({
    onSuccess: (response: object | ResponseObject) => {
      // Only redirect if there's no error code in the response
      if (!('code' in response)) {
        // Fetch updated user data since remove endpoint returns no data
        fetchUser();

        // Invalidate relevant queries to trigger refetch with new team data
        queryClient.invalidateQueries({ queryKey: ['team-get'] });
        queryClient.invalidateQueries({ queryKey: ['client-campaigns-search'] });
        queryClient.invalidateQueries({ queryKey: ['client-campaign'] });

        router.push(fixUrlForCloudFront('/'));
      }
    },
    teamId: user?.team?.team_id || '',
    userId: user?.user_id
  });

  useEffect(() => {
    if (data?.teams?.length) {
      setTeams(data?.teams || []);
      setMembers(data?.members || []);
      setBrands(data?.brands || []);
    }
  }, [data]);

  const isAdmin = user.team?.role === 'ADMIN';

  const teamsMemo = useMemo(() => teams, [teams]);
  const membersMemo = useMemo(
    () => members.filter((member) => member.status === 'MEMBER' || isAdmin),
    [members, isAdmin]
  );
  const brandsMemo = useMemo(() => brands, [brands]);

  const handleTeamLeave = (e: TypeAny) => {
    setLeaveConfirm(false);
    e.preventDefault();
    teamMemberRemove();
  };

  return (
    <>
      {leaveConfirm && (
        <Confirm handleCancel={() => setLeaveConfirm(false)} handleConfirm={handleTeamLeave}>
          {t('team.confirmLeave')}
        </Confirm>
      )}

      <div className={styles.team}>
        <Wrapper
          isLoading={isLoadingTeams}
          heading={
            <div className={styles.teamHeading}>
              <TeamHeader isLoadingTeams={isLoadingTeams} setTeams={setTeams} teams={teamsMemo} />
            </div>
          }
        >
          <TeamBrands setBrands={setBrands} brands={brandsMemo} />

          {teamsMemo.length > 0 ? (
            <>
              <div className={styles.teamMembersHeading}>
                <Typography sizes={['h4']}>{t('team.membersHeading')}</Typography>

                {user.permissions?.can_invite_team_members && (
                  <div className={styles.teamMembersHeadingCta}>
                    {membersMemo.length === 0 && (
                      <div className={styles.teamMembersHeadingCount}>
                        {`${membersMemo.length} ${t('team.membersHeadingCount')}`}
                      </div>
                    )}

                    <div className={styles.teamMembersHeadingButtons}>
                      <Button
                        onClick={() => setLeaveConfirm(true)}
                        disabled={membersMemo.length === 1}
                        isLoading={isRemovingMember}
                        theme="red"
                      >
                        {t('team.membersHeadingLeave')}
                      </Button>

                      <Button
                        onClick={() => setIsInvitingMember(true)}
                        disabled={isRemovingMember}
                        theme="cta"
                      >
                        {`+ ${t('team.membersHeadingAdd')}`}
                      </Button>
                    </div>
                  </div>
                )}

                {isInvitingMember && (
                  <Modal handleClose={() => setIsInvitingMember(false)}>
                    <TeamInvite
                      onSuccess={() => setIsInvitingMember(false)}
                      setMembers={setMembers}
                    />
                  </Modal>
                )}
              </div>

              <div className={styles.teamMembersList}>
                {membersMemo.length > 0 ? (
                  membersMemo?.map((member) => (
                    <TeamMember
                      key={`team-member-${member?.user_id}`}
                      member={member}
                      setMembers={setMembers}
                    />
                  ))
                ) : (
                  <Typography sizes={['h4']}>{t('team.noMembers')}</Typography>
                )}
              </div>

              <TeamBrandSettingsSection
                isAdmin={isAdmin}
                members={members}
                setMembers={setMembers}
                teamId={user?.team?.team_id || ''}
                viewerUserId={user?.user_id ?? ''}
              />
            </>
          ) : (
            <div className={styles.teamNotFound}>
              <NotFound text={t('team.notFound')} />
            </div>
          )}
        </Wrapper>
      </div>
    </>
  );
}
